import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Title from "./TabTitle";
import ChitRequest from "../../pages/ChitRequest/ChitRequest";
import AddTime from "../../pages/Time/AddTime";
import TimeRecorded from "../../components/Time/TimeRecorded";
import { useNavigate, useMatch } from "react-router-dom";
import { addTab, removeTab, useTabs, TAB_TYPES } from "slices/tabsSlice";
import MyMattersPageComponent from "components/Matters/MattersGridComponent";
import { Tabs } from "components/Enums/TabEnums.ts";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import AccessRevokedModal from "components/common/AccessRevokedModal";
import Bills from "pages/Bills/Bills";
import { useCurrentUser } from "services/currentUserService";
import Authorizations from "pages/ChitPosting/Authorizations";

const FeeEarnerTabs = React.memo((props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [selected, setSelected] = useState(-1);

  const tabsData = useTabs(TAB_TYPES.FEEEARNER);
  const { allowed } = useAllowedLiscence(Tabs.FEE_EARNER);
  const { data: user } = useCurrentUser();

  const findContent = (item) => {
    switch (item) {
      case "chitrequest":
        return "Chit Request";
      case "timerecording":
        return "Time Recording";
      case "timerecorded":
        return "Time Recorded";
      case "mymatters":
        return "My Matters";
      case "billingrequest":
        return "Billing Request";
      case "authorisations":
        return "Authorisations";
      default:
        break;
    }
  };

  const urlDefiner = (e) => {
    if (params !== null) {
      let url = tabs[e]?.toLowerCase();
      if (url === undefined) {
        url = "";
      }
      navigate(`/feeearner/${url}`);
    }
  };

  const params = useMatch("feeearner/:data");

  useEffect(() => {
    if (params !== null) {
      let data = params?.params?.data;
      const dataLower = data?.toLowerCase();
      if (!tabsData.includes(dataLower)) {
        dispatch(addTab("feeearner", dataLower));
      } else {
        setSelected(tabsData.indexOf(dataLower));
      }
    }
  }, [params, tabsData]);

  useEffect(() => {
    if (tabsData) {
      setTabs(tabsData);
      const segment = params?.params?.data?.toLowerCase();
      const index = segment ? tabsData.indexOf(segment) : -1;
      setSelected(index === -1 ? 0 : index);
    }
  }, [tabsData, params?.params?.data]);

  const removeTabDispatcher = (tab) => {
    const closedIndex = tabs.findIndex((t) => findContent(t) === tab);
    if (closedIndex === -1) return;

    const closedTabKey = tabs[closedIndex];
    const dispatchRemove = () => {
      queueMicrotask(() => {
        dispatch(removeTab("feeearner", closedTabKey));
      });
    };

    const newTabs = tabs.filter((t) => findContent(t) !== tab);
    if (!newTabs.length) {
      navigate("/");
      setSelected(0);
      dispatchRemove();
      return;
    }
    const newSelected =
      closedIndex === selected
        ? Math.min(selected, newTabs.length - 1)
        : closedIndex < selected
          ? selected - 1
          : selected;
    setSelected(newSelected);
    navigate(`/feeearner/${newTabs[newSelected]?.toLowerCase() ?? ""}`);
    dispatchRemove();
  };

  const handleSelect = (e) => {
    setSelected(e.selected);
    urlDefiner(e.selected);
  };

  return allowed ? (
    <TabStrip
      selected={selected}
      onSelect={handleSelect}
      keepTabsMounted={true}
      className="navigationTabs h-100">
      {tabs.map((item) => {
        return (
          item && (
            <TabStripTab
              title={
                <Title
                  content={findContent(item)}
                  onTabRemove={removeTabDispatcher}
                  selected={findContent(tabs.at(selected))}
                  count={tabs.length}
                />
              }
              key={item}>
              <div className="h-100 w-100">
                {item === "chitrequest" && <ChitRequest />}
                {item === "timerecorded" && (
                  <TimeRecorded
                    className="page"
                    fromCaseFile={false}
                    parameter={user.userRef}
                  />
                )}
                {item === "timerecording" && <AddTime fromFeeEarner={true} />}
                {item === "mymatters" && (
                  <MyMattersPageComponent component={"myMattersPage"} />
                )}

                {item === "billingrequest" && <Bills component={"BillingRequest"} />}
                {item === "authorisations" && <Authorizations />}
              </div>
            </TabStripTab>
          )
        );
      })}
    </TabStrip>
  ) : (
    <AccessRevokedModal removeTab={() => props.closeTab()} />
  );
});

export default FeeEarnerTabs;
